// PRD-01 US-AUTH-06. /reset-password?token=...

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "@/lib/api";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md bg-white border border-red-200 rounded-2xl p-6 text-center">
          <h1 className="text-xl font-semibold mb-2 text-red-800">
            {t("auth.reset.noToken", "Token ontbreekt")}
          </h1>
          <Link to="/forgot-password" className="text-brand-600 hover:text-brand-700 text-sm">
            {t("auth.reset.newRequest", "Nieuwe resetlink aanvragen")}
          </Link>
        </div>
      </main>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setErrorKey("auth.register.passwordMismatch");
      return;
    }
    setErrorKey(null);
    setLoading(true);
    try {
      await api.post(
        "/v1/auth/reset-password",
        { token, newPassword: password },
        { skipAuth: true }
      );
      setDone(true);
    } catch (err) {
      setErrorKey(err instanceof ApiError ? err.i18nKey : "errors.internal");
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4 py-12">
        <div className="max-w-md bg-white rounded-2xl shadow-sm border border-neutral-200 p-8 text-center">
          <h1 className="text-xl font-semibold mb-2">
            {t("auth.reset.done", "Wachtwoord aangepast")}
          </h1>
          <p className="text-sm text-neutral-700 mb-4">
            {t(
              "auth.reset.doneBody",
              "Je wachtwoord is gewijzigd. Alle actieve sessies zijn beëindigd."
            )}
          </p>
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg"
          >
            {t("auth.reset.login", "Inloggen")}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-brand-50 to-white">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-neutral-200 p-8">
        <h1 className="text-2xl font-semibold mb-1">
          {t("auth.reset.title", "Nieuw wachtwoord instellen")}
        </h1>
        <p className="text-sm text-neutral-600 mb-6">
          {t("auth.reset.subtitle", "Kies een sterk wachtwoord. Het mag niet hetzelfde zijn als de laatste 3.")}
        </p>

        <form onSubmit={onSubmit} className="space-y-4" aria-busy={loading}>
          <div>
            <label htmlFor="newpw" className="block text-sm font-medium text-neutral-700 mb-1">
              {t("auth.reset.newPassword", "Nieuw wachtwoord")}
            </label>
            <input
              id="newpw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={12}
              autoFocus
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
            <p className="mt-1 text-xs text-neutral-500">
              {t("auth.register.passwordHint", "Minimaal 12 tekens, met hoofdletter, cijfer en speciaal teken")}
            </p>
          </div>
          <div>
            <label htmlFor="confirm" className="block text-sm font-medium text-neutral-700 mb-1">
              {t("auth.register.passwordConfirm", "Wachtwoord herhalen")}
            </label>
            <input
              id="confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
          </div>

          {errorKey && (
            <div role="alert" className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
              {t(errorKey)}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-medium rounded-lg"
          >
            {loading
              ? t("auth.reset.saving", "Opslaan…")
              : t("auth.reset.submit", "Wachtwoord opslaan")}
          </button>
        </form>
      </div>
    </main>
  );
}
